import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { font, Button, Badge } from "@realtyflows/ui";
import { useTheme } from "../contexts/ThemeContext";
import { getContractTemplates, getContractFolder, updateContractFolder, updateFolderStatus, addFolderForm, deleteFolderForm, reorderFolderForms, sendFolderEnvelope, getDeals, getFolderStates, getFolderTemplates, applyFolderTemplate as applyFolderTemplateApi, aiCoverSheet, submitFolderForReview } from "../api";
import SearchSelect from "../components/SearchSelect";
import FolderUploads from "../components/FolderUploads";
import FolderStatusBadge from "../components/FolderStatusBadge";
import FolderActivityFeed from "../components/FolderActivityFeed";
import FolderShareModal from "../components/FolderShareModal";
import FolderCompliance from "../components/FolderCompliance";
import HealthScore from "../components/HealthScore";
import OfferBoard from "../components/OfferBoard";
import FolderTimeline from "../components/FolderTimeline";
import FolderCommission from "../components/FolderCommission";
import MlsLookup from "../components/MlsLookup";
import useResponsive from "../hooks/useResponsive";

const FORM_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "Representation", label: "Representation" },
  { key: "Listing", label: "Listing" },
  { key: "Purchase", label: "Purchase" },
  { key: "Negotiation", label: "Negotiation" },
  { key: "Lease", label: "Lease" },
];

export default function FolderDetail({ user }) {
  const t = useTheme();  // V2 ADR-003 Phase 4.3 wave 8
  const { folderId } = useParams();
  const navigate = useNavigate();
  const responsive = useResponsive();

  const [folder, setFolder] = useState(null);
  const [folderForms, setFolderForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allForms, setAllForms] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formCategory, setFormCategory] = useState("all");
  const [editingName, setEditingName] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [toast, setToast] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendForm, setSendForm] = useState({ deal_id: "", signer1Name: "", signer1Email: "", signer2Name: "", signer2Email: "" });
  const [sending, setSending] = useState(false);
  const [deals, setDeals] = useState([]);
  // V1 Section B: lifecycle state catalog (loaded once)
  const [folderStates, setFolderStates] = useState([]);
  // V1 Section B: folder templates (Buyer/Seller bundles), loaded for the apply menu
  const [bundleTemplates, setBundleTemplates] = useState([]);
  const [showBundleMenu, setShowBundleMenu] = useState(false);
  // V1 Section B: share modal toggle
  const [showShareModal, setShowShareModal] = useState(false);
  // V4 Features 4.1/4.2: collapsible MLS auto-fill panel
  const [showMlsPanel, setShowMlsPanel] = useState(false);
  // V2 Feature 2.4: AI envelope cover sheet (editable subject + body)
  const [coverSheet, setCoverSheet] = useState({ subject: "", body: "" });
  const [coverSheetLoading, setCoverSheetLoading] = useState(false);

  // V3 Feature 3.3: agent submits the folder to their broker for review
  const handleSubmitForReview = async () => {
    if (!window.confirm("Submit this folder for broker review? You won't be able to make changes until the broker approves or requests revisions.")) return;
    try {
      await submitFolderForReview(folderId);
      setFolder((curr) => ({ ...curr, status: "pending_review" }));
      showToast("Submitted for broker review");
    } catch (err) {
      showToast(err.message || "Could not submit", "error");
    }
  };

  const handleGenerateCoverSheet = async () => {
    setCoverSheetLoading(true);
    try {
      const draft = await aiCoverSheet(folderId, sendForm.signer1Name || null);
      if (draft) {
        setCoverSheet({ subject: draft.subject || "", body: draft.body || "" });
        showToast("AI cover sheet generated");
      } else {
        showToast("AI couldn't generate cover sheet", "error");
      }
    } catch (err) {
      showToast(err.message || "Cover sheet generation failed", "error");
    } finally {
      setCoverSheetLoading(false);
    }
  };

  /**
   * Apply a folder template (e.g. "Buyer Starter" — Rep Agreement + Earnest Money + …).
   * Refetches folder afterwards so the new forms appear in order.
   */
  const handleApplyTemplate = async (folderTemplateId, templateName) => {
    setShowBundleMenu(false);
    try {
      const result = await applyFolderTemplateApi(folderId, folderTemplateId);
      const addedCount = result?.added?.length || 0;
      const skipped = result?.skipped || 0;
      const msg = addedCount > 0
        ? `Added ${addedCount} form${addedCount === 1 ? "" : "s"} from "${templateName}"${skipped ? ` (${skipped} already in folder)` : ""}`
        : `All forms from "${templateName}" are already in this folder`;
      showToast(msg, addedCount > 0 ? "success" : "info");
      await fetchFolder();
    } catch (err) {
      showToast(err.message || "Could not apply template", "error");
    }
  };

  // Save a status change to the server. Local state is updated optimistically
  // and reverted on error so the dropdown stays in sync.
  const handleStatusChange = async (newStatus) => {
    const prev = folder?.status;
    if (!prev || prev === newStatus) return;
    setFolder((curr) => ({ ...curr, status: newStatus }));
    try {
      await updateFolderStatus(folderId, newStatus);
      showToast(`Status changed to ${newStatus.replace(/_/g, " ")}`);
    } catch (err) {
      setFolder((curr) => ({ ...curr, status: prev }));
      showToast(err.message || "Could not change status", "error");
    }
  };

  const fetchFolder = async () => {
    try {
      const data = await getContractFolder(folderId);
      setFolder(data);
      setFolderForms(data.forms || []);
      setFolderName(data.name);
    } catch { navigate("/contracts"); }
    setLoading(false);
  };

  useEffect(() => {
    fetchFolder();
    getContractTemplates().then(setAllForms).catch(() => {});
    getFolderStates().then(setFolderStates).catch(() => setFolderStates([]));
    getFolderTemplates().then(setBundleTemplates).catch(() => setBundleTemplates([]));
  }, [folderId]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (msg, variant = "success") => setToast({ msg, variant });

  const handleRename = async () => {
    if (!folderName.trim()) return;
    try {
      await updateContractFolder(folderId, { name: folderName.trim() });
      setFolder(prev => ({ ...prev, name: folderName.trim() }));
      setEditingName(false);
      showToast("Folder renamed");
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleAddForm = async (form) => {
    try {
      const added = await addFolderForm(folderId, form.id);
      setFolderForms(prev => [...prev, added]);
      setShowAddForm(false);
      showToast(`${form.name} added`);
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleRemoveForm = async (formId) => {
    try {
      await deleteFolderForm(folderId, formId);
      setFolderForms(prev => prev.filter(f => f.id !== formId));
      showToast("Form removed");
    } catch (err) { showToast(err.message, "error"); }
  };

  const handleMoveForm = async (fromIdx, toIdx) => {
    const forms = [...folderForms];
    const [moved] = forms.splice(fromIdx, 1);
    forms.splice(toIdx, 0, moved);
    setFolderForms(forms);
    try {
      await reorderFolderForms(folderId, forms.map(f => f.id));
    } catch {}
  };

  const openSendModal = async () => {
    const readyForms = folderForms.filter(f => f.status === "ready" || f.status === "filled");
    if (readyForms.length === 0) {
      showToast("Fill and prepare at least one form before sending", "error");
      return;
    }
    try { const d = await getDeals(); setDeals(d); } catch {}
    setShowSendModal(true);
  };

  // V1 Section B: send the whole folder as one envelope.
  // Server bundles all template-derived forms + uploads with `include_in_envelope=true`,
  // counts as a single quota unit, sets folder status → 'active', and logs activity.
  const handleSendEnvelope = async () => {
    if (!sendForm.signer1Email) { showToast("Signer 1 email is required", "error"); return; }
    setSending(true);
    try {
      const signers = [{ name: sendForm.signer1Name, email: sendForm.signer1Email, role: "Signer 1", order: 1 }];
      if (sendForm.signer2Email) {
        signers.push({ name: sendForm.signer2Name, email: sendForm.signer2Email, role: "Signer 2", order: 2 });
      }

      const result = await sendFolderEnvelope(folderId, {
        signers,
        // V2 Feature 2.4: pass the cover sheet through if the agent generated/edited one
        subject: coverSheet.subject || undefined,
        message: coverSheet.body || undefined,
      });

      // Server already updated status; mirror locally so the badge updates immediately.
      setFolder((prev) => ({ ...prev, status: "active", esign_envelope_id: result.envelopeId }));
      showToast(`Envelope sent — ${result.docCount} doc${result.docCount === 1 ? "" : "s"} to ${result.signerCount} signer${result.signerCount === 1 ? "" : "s"}`);
      setShowSendModal(false);
    } catch (err) {
      showToast(err.message || "Failed to send", "error");
    }
    setSending(false);
  };

  const filteredForms = useMemo(() => {
    if (formCategory === "all") return allForms;
    return allForms.filter(f => f.category === formCategory);
  }, [allForms, formCategory]);

  const folderFormIds = useMemo(() => new Set(folderForms.map(f => f.template_id)), [folderForms]);

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.textMuted }}>Loading...</div>;
  if (!folder) return null;

  const readyCount = folderForms.filter(f => f.status === "filled" || f.status === "ready").length;

  return (
    <div style={{ animation: "fadeSlide 0.3s ease" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate("/contracts")} style={{ background: "none", border: "none", color: t.textSecondary, fontSize: 13, cursor: "pointer", fontFamily: font.body, fontWeight: 600 }}>
          &larr; Back to Folders
        </button>
        <span style={{ color: t.border }}>|</span>
        {editingName ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") setEditingName(false); }} autoFocus
              style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, fontFamily: font.display, border: `1px solid ${t.border}`, borderRadius: 6, padding: "4px 10px", outline: "none", width: 300 }} />
            <Button variant="primary" size="sm" onClick={handleRename}>Save</Button>
            <Button variant="outline" size="sm" onClick={() => setEditingName(false)}>Cancel</Button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>{folder.name}</div>
            <button onClick={() => setEditingName(true)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }} title="Rename">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
            </button>
          </div>
        )}
      </div>

      {/* Stats Bar */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, padding: "12px 16px", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}><strong style={{ color: t.textPrimary }}>{folderForms.length}</strong> form(s)</div>
        <div style={{ width: 1, height: 20, background: t.border }} />
        <div style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}><strong style={{ color: t.emerald }}>{readyCount}</strong> ready</div>
        <div style={{ width: 1, height: 20, background: t.border }} />
        {/* V1 Section B: lifecycle status — badge + inline change dropdown */}
        <FolderStatusBadge status={folder.status} />
        {folderStates.length > 0 && (
          <select
            value={folder.status || "draft"}
            onChange={(e) => handleStatusChange(e.target.value)}
            title="Change folder status"
            style={{
              fontSize: 11, padding: "4px 8px", borderRadius: 6,
              border: `1px solid ${t.border}`, background: t.surface,
              color: t.textSecondary, fontFamily: font.body, cursor: "pointer",
            }}
          >
            {folderStates.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        )}
        <div style={{ flex: 1 }} />
        {/* V4 Features 4.1/4.2: MLS auto-fill toggle */}
        <Button variant="outline" size="sm" onClick={() => setShowMlsPanel((v) => !v)}>
          {showMlsPanel ? "Hide MLS lookup" : "📍 MLS auto-fill"}
        </Button>
        {/* V3 Feature 3.3: Submit for broker review — only when team-scoped + not already pending/closed */}
        {folder?.team_id && folder.status !== "pending_review" && folder.status !== "closed" && folder.status !== "archived" && (
          <Button variant="outline" size="sm" onClick={handleSubmitForReview}>
            Submit for Review
          </Button>
        )}
        {/* V1 Section B: Share folder (basic ACL — owner can grant editor/viewer access) */}
        <Button variant="outline" size="sm" onClick={() => setShowShareModal(true)}>Share</Button>
        {/* V1 Section B: Apply a template bundle (Buyer Starter, Seller Starter, etc.) */}
        {bundleTemplates.length > 0 && (
          <div style={{ position: "relative" }}>
            <Button variant="outline" size="sm" onClick={() => setShowBundleMenu((v) => !v)}>
              Apply template ▾
            </Button>
            {showBundleMenu && (
              <>
                <div onClick={() => setShowBundleMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
                <div style={{
                  position: "absolute", top: 36, right: 0, zIndex: 100,
                  background: t.surface, border: `1px solid ${t.border}`,
                  borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
                  minWidth: 240, padding: "6px 0",
                }}>
                  {bundleTemplates.map((tpl) => (
                    <div
                      key={tpl.id}
                      onClick={() => handleApplyTemplate(tpl.id, tpl.name)}
                      onMouseEnter={(e) => e.currentTarget.style.background = t.surfaceWarm}
                      onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      style={{ padding: "8px 14px", cursor: "pointer", fontFamily: font.body }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary }}>{tpl.name}</div>
                      {tpl.description && (
                        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>{tpl.description}</div>
                      )}
                      <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3, fontFamily: font.mono }}>
                        {tpl.form_count || 0} form{tpl.form_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        <Button variant="primary" size="sm" onClick={() => setShowAddForm(true)}>+ Add Form</Button>
        {folderForms.length > 0 && (
          <Button variant={folder.status === "sent" ? "outline" : "primary"} size="sm" onClick={openSendModal} disabled={folder.status === "sent"}
            style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            {folder.status === "sent" ? "Envelope Sent" : "Send Envelope"}
          </Button>
        )}
      </div>

      {/* Forms List */}
      {folderForms.length === 0 ? (
        <div style={{ padding: 50, textAlign: "center", background: t.surface, border: `1.5px dashed ${t.border}`, borderRadius: 12 }}>
          <div style={{ fontSize: 40, opacity: 0.15, marginBottom: 12 }}>{"\uD83D\uDCC4"}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, marginBottom: 8 }}>No forms in this folder yet</div>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 20, lineHeight: 1.6 }}>Add forms from the library, fill them out, then send all at once.</div>
          <Button variant="primary" onClick={() => setShowAddForm(true)}>+ Add Form</Button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {folderForms.map((form, idx) => (
            <div key={form.id} style={{
              background: t.surface, border: `1px solid ${t.border}`, borderRadius: 8,
              padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
              borderLeft: `3px solid ${form.status === "ready" ? t.emerald : form.status === "filled" ? t.sky : form.status === "signed" ? t.emerald : t.border}`,
            }}>
              {/* Order arrows */}
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                {idx > 0 && <button onClick={() => handleMoveForm(idx, idx - 1)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 0, lineHeight: 1 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="18 15 12 9 6 15"/></svg></button>}
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, fontFamily: "monospace", textAlign: "center", width: 16 }}>{idx + 1}</div>
                {idx < folderForms.length - 1 && <button onClick={() => handleMoveForm(idx, idx + 1)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 0, lineHeight: 1 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg></button>}
              </div>

              {/* Icon */}
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>

              {/* Name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.template_name || form.name}</div>
                <div style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", textTransform: "uppercase", letterSpacing: 0.5 }}>{form.category}</div>
              </div>

              {/* Status + tags */}
              <Badge variant={form.status === "ready" ? "emerald" : form.status === "filled" ? "sky" : "default"}>
                {form.status === "ready" ? "Ready" : form.status === "filled" ? "Filled" : form.status === "signed" ? "Signed" : "Draft"}
              </Badge>
              {form.esign_labels?.length > 0 && <div style={{ fontSize: 9, color: t.emerald, fontWeight: 700, fontFamily: "monospace" }}>{form.esign_labels.length} tags</div>}

              {/* Actions */}
              <div style={{ display: "flex", gap: 6 }}>
                <Button variant="primary" size="sm" onClick={() => navigate(`/contracts/templates/${form.template_id}`)}>
                  {form.status === "draft" ? "Fill" : "Edit"}
                </Button>
                <Button variant="outline" size="sm" onClick={() => navigate(`/contracts/folders/${folderId}/prepare/${form.id}?templateId=${form.template_id}`)}
                  style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                  E-Sign
                </Button>
                <button onClick={() => handleRemoveForm(form.id)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: 4 }} title="Remove">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* V4 Features 4.1/4.2: MLS auto-fill panel (collapsible) */}
      {showMlsPanel && (
        <div style={{ marginTop: 16, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              MLS Auto-Fill
            </span>
            <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
              Look up by MLS# and fill every form in this folder
            </span>
          </div>
          <MlsLookup folderId={folderId} onApplied={fetchFolder} />
        </div>
      )}

      {/* V2 Feature 2.5: Horizontal event timeline — quick visual progress */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 14, marginTop: 16 }}>
        <FolderTimeline folderId={folderId} />
      </div>

      {/* V2 Features 2.3/2.8/2.9: Health score panel + Compliance side-by-side */}
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "1fr 2fr", gap: 20, marginTop: 24 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>
            Folder Health
          </div>
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
            <HealthScore folderId={folderId} />
          </div>
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              Compliance Checklist
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
              Required documents auto-detected
            </div>
          </div>
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
            <FolderCompliance folderId={folderId} />
          </div>
        </div>
      </div>

      {/* V3 Features 3.12/3.13/3.14: Commission ledger — visible when folder is past Active stage */}
      {folder?.deal_id && ["in_escrow", "pending_review", "closed"].includes(folder.status) && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              Commissions & Disbursements
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
              Track splits, generate CDA, mark payments confirmed
            </div>
          </div>
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
            <FolderCommission folderId={folderId} />
          </div>
        </div>
      )}

      {/* V2 Feature 2.11: Offer Board — only relevant when the folder is linked to a deal */}
      {folder?.deal_id && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              Offers
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
              Track every offer received — accept, counter, or reject
            </div>
          </div>
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 16 }}>
            <OfferBoard dealId={folder.deal_id} />
          </div>
        </div>
      )}

      {/* V1 Section B: Uploads + Activity Feed in a 2-column layout */}
      <div style={{ display: "grid", gridTemplateColumns: responsive.isCompact ? "1fr" : "2fr 1fr", gap: 20, marginTop: 24 }}>
        {/* Uploads */}
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
              Uploaded Documents
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
              Pre-approval letters, inspection reports, scans
            </div>
          </div>
          <FolderUploads folderId={folderId} />
        </div>

        {/* Activity feed */}
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 12, fontFamily: font.display }}>
            Activity
          </div>
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10, padding: 14 }}>
            <FolderActivityFeed folderId={folderId} />
          </div>
        </div>
      </div>

      {/* Add Form Modal */}
      {showAddForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 560, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>Add Form to Folder</div>
              <button onClick={() => setShowAddForm(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: t.textMuted }}>&times;</button>
            </div>
            <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap" }}>
              {FORM_CATEGORIES.map(cat => (
                <button key={cat.key} onClick={() => setFormCategory(cat.key)} style={{
                  padding: "5px 12px", fontSize: 10, fontWeight: formCategory === cat.key ? 700 : 500,
                  color: formCategory === cat.key ? t.emerald : t.textSecondary,
                  background: formCategory === cat.key ? `${t.emerald}12` : "transparent",
                  border: `1px solid ${formCategory === cat.key ? `${t.emerald}40` : "transparent"}`,
                  borderRadius: 6, cursor: "pointer", fontFamily: font.body,
                }}>{cat.label}</button>
              ))}
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {filteredForms.map(form => {
                const alreadyAdded = folderFormIds.has(form.id);
                return (
                  <div key={form.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: `1px solid ${t.border}`, opacity: alreadyAdded ? 0.5 : 1 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: `linear-gradient(135deg, ${t.navyLight}, ${t.navyMid})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{form.name}</div>
                      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", textTransform: "uppercase" }}>{form.category}</div>
                    </div>
                    <Button variant={alreadyAdded ? "outline" : "primary"} size="sm" disabled={alreadyAdded} onClick={() => handleAddForm(form)}>
                      {alreadyAdded ? "Added" : "+ Add"}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Send Envelope Modal */}
      {showSendModal && (() => {
        const readyForms = folderForms.filter(f => f.status === "ready" || f.status === "filled");
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: t.surface, borderRadius: 16, padding: 28, maxWidth: 480, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>Send Envelope</div>
              <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body, marginBottom: 16 }}>{readyForms.length} document(s) will be sent as one signing request</div>

              <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: 12, marginBottom: 16, maxHeight: 120, overflowY: "auto" }}>
                {readyForms.map((form, i) => (
                  <div key={form.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 12, fontFamily: font.body, color: t.textPrimary }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, fontFamily: "monospace", width: 18 }}>{i + 1}.</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    {form.template_name || form.name}
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: t.textSecondary, fontFamily: font.body, display: "block", marginBottom: 4 }}>Link to Deal (optional)</label>
                <SearchSelect options={deals.map(d => ({ value: d.id, label: `${d.client_name || "Deal"} — ${d.property_address || d.status}` }))} value={sendForm.deal_id} onChange={v => setSendForm({ ...sendForm, deal_id: v })} placeholder="Search deals..." />
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", marginBottom: 6, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#16a34a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>1</div>
                SIGNER 1
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <input value={sendForm.signer1Name} onChange={e => setSendForm({ ...sendForm, signer1Name: e.target.value })} placeholder="Full Name" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
                <input value={sendForm.signer1Email} onChange={e => setSendForm({ ...sendForm, signer1Email: e.target.value })} placeholder="Email *" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", marginBottom: 6, fontFamily: "monospace", display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>2</div>
                SIGNER 2 (optional)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
                <input value={sendForm.signer2Name} onChange={e => setSendForm({ ...sendForm, signer2Name: e.target.value })} placeholder="Full Name" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
                <input value={sendForm.signer2Email} onChange={e => setSendForm({ ...sendForm, signer2Email: e.target.value })} placeholder="Email" style={{ padding: "9px 12px", borderRadius: 8, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none" }} />
              </div>

              {/* V2 Feature 2.4: AI cover sheet — friendly intro shown to the client */}
              <div style={{ marginBottom: 16, padding: 12, background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, fontFamily: font.body }}>
                    Cover note for signer (optional)
                  </span>
                  <Button variant="outline" size="sm" onClick={handleGenerateCoverSheet} disabled={coverSheetLoading}>
                    {coverSheetLoading ? "Thinking..." : "✨ AI generate"}
                  </Button>
                </div>
                <input
                  value={coverSheet.subject}
                  onChange={(e) => setCoverSheet({ ...coverSheet, subject: e.target.value })}
                  placeholder="Subject line"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, marginBottom: 6, outline: "none", background: t.surface, color: t.textPrimary }}
                />
                <textarea
                  value={coverSheet.body}
                  onChange={(e) => setCoverSheet({ ...coverSheet, body: e.target.value })}
                  placeholder="Cover note body — agent's intro to the signer..."
                  rows={4}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: `1px solid ${t.border}`, fontSize: 12, fontFamily: font.body, outline: "none", background: t.surface, color: t.textPrimary, resize: "vertical" }}
                />
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="outline" onClick={() => setShowSendModal(false)} style={{ flex: 1 }}>Cancel</Button>
                <Button variant="primary" onClick={handleSendEnvelope} disabled={sending} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  {sending ? "Sending..." : `Send ${readyForms.length} Document(s)`}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* V1 Section B: Share modal */}
      {showShareModal && (
        <FolderShareModal folderId={folderId} onClose={() => setShowShareModal(false)} />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: toast.variant === "error" ? "#fee2e2" : "#dcfce7",
          color: toast.variant === "error" ? "#dc2626" : "#16a34a",
          padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          fontFamily: font.body, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", zIndex: 9999,
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
